import { useState } from "react";
import { AlertTriangle, CheckCircle2, Download, Puzzle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AUTO_POST_MIN_VERSION, GAP_OPTIONS, HELPER_ZIP, helperVersion, versionAtLeast } from "@/lib/helper";

const STEPS = [
  <>Muat turun fail <b className="text-slate-200">postpintar-helper.zip</b> dan <b className="text-slate-200">extract</b> (Extract All) ke satu folder.</>,
  <>Buka Chrome / Edge, taip <code className="text-slate-200">chrome://extensions</code> di bar alamat.</>,
  <>Hidupkan <b className="text-slate-200">Developer mode</b> (penjuru kanan atas).</>,
  <>Tekan <b className="text-slate-200">Load unpacked</b> dan pilih folder <b className="text-slate-200">postpintar-helper</b>.</>,
  <>Kembali ke halaman ini dan tekan <b className="text-slate-200">F5</b> (refresh). Status akan bertukar kepada "Helper aktif".</>,
];

export function HelperInstallDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border bg-card" data-testid="helper-install-dialog">
        <DialogHeader>
          <DialogTitle>Pasang PostPintar Helper</DialogTitle>
          <DialogDescription>Untuk Chrome atau Edge di komputer. Sekali pasang sahaja.</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 text-sm text-muted-foreground">
          {STEPS.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 font-mono text-xs text-primary">{i + 1}</span>
              <span className="leading-relaxed">{s}</span>
            </li>
          ))}
        </ol>
        <Button asChild className="w-full" data-testid="helper-download-btn">
          <a href={HELPER_ZIP} download><Download className="mr-2 h-4 w-4" /> Muat turun postpintar-helper.zip</a>
        </Button>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Helper hanya mengisi teks & gambar dalam kotak "Cipta siaran". Butang <b className="text-slate-200">Siaran</b> tetap anda yang tekan. Pastikan anda sudah log masuk Facebook di Chrome.
        </p>
      </DialogContent>
    </Dialog>
  );
}

export default function HelperBar({ autoNext, setAutoNext, autoPost = false, setAutoPost = () => {}, gap = "rand", setGap = () => {} }) {
  const gapLabel = GAP_OPTIONS.find((o) => o.v === gap)?.l || "1-3 minit (rawak)";
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const version = helperVersion();
  const canAutoPost = versionAtLeast(version, AUTO_POST_MIN_VERSION);

  if (!version) {
    return (
      <div className="mb-4 flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid="helper-not-installed">
        <div className="flex items-start gap-3">
          <Puzzle className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div>
            <p className="text-sm font-medium">Nak teks terus masuk dalam "Cipta siaran"?</p>
            <p className="text-xs text-muted-foreground">Pasang PostPintar Helper (Chrome). Popup group dibuka, teks & gambar diisi automatik, anda cuma tekan Siaran.</p>
          </div>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="helper-install-btn"><Download className="mr-1.5 h-4 w-4" /> Pasang Helper</Button>
        <HelperInstallDialog open={open} onOpenChange={setOpen} />
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3" data-testid="helper-active">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
          <div>
            <p className="text-sm font-medium text-emerald-100">PostPintar Helper aktif <span className="font-mono text-xs text-emerald-300/80">v{version}</span></p>
            <p className="text-xs text-emerald-200/70">{autoPost ? `Teks diisi & Siaran ditekan automatik. Jarak ${gapLabel} antara group.` : "Teks & gambar diisi automatik dalam \"Cipta siaran\". Anda cuma tekan Siaran."}</p>
          </div>
        </div>
        <div className="flex flex-col gap-2 text-xs text-emerald-100 sm:items-end">
          <label className="flex items-center gap-2">
            Auto tekan Siaran
            <Switch checked={autoPost} onCheckedChange={(on) => (on ? setConfirm(true) : setAutoPost(false))} disabled={!canAutoPost} data-testid="helper-auto-post-toggle" />
          </label>
          <label className="flex items-center gap-2">
            Auto ke group seterusnya
            <Switch checked={autoNext} onCheckedChange={setAutoNext} disabled={autoPost} data-testid="helper-auto-next-toggle" />
          </label>
          <div className="flex items-center gap-2">
            <span>Jarak antara group</span>
            <Select value={gap} onValueChange={setGap} disabled={!autoNext && !autoPost}>
              <SelectTrigger className="h-8 w-[150px] border-emerald-500/30 bg-background text-xs text-slate-100" data-testid="helper-gap-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {GAP_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`helper-gap-${o.v}`}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      {!canAutoPost && (
        <p className="mt-3 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100" data-testid="helper-update-notice">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Auto tekan Siaran perlukan Helper v1.1.0. <button type="button" onClick={() => setOpen(true)} className="underline">Muat turun versi baharu</button>, extract ganti folder lama, kemudian tekan ikon muat semula (reload) di chrome://extensions dan F5 halaman ini.</span>
        </p>
      )}
      <HelperInstallDialog open={open} onOpenChange={setOpen} />
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="border-border bg-card" data-testid="helper-auto-post-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-400" /> Hidupkan Auto tekan Siaran?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Helper akan isi teks <b className="text-slate-200">dan tekan Siaran sendiri</b> untuk setiap group, dengan jarak <b className="text-slate-200">{gapLabel}</b> (boleh ditukar).</p>
                <p className="text-amber-200">Risiko: Facebook mungkin anggap ini automasi/spam dan menghadkan atau menyekat akaun anda. Anda bertanggungjawab atas akaun sendiri.</p>
                <p>Tip: guna teks berbeza (variasi AI) untuk setiap group, dan jangan post ke terlalu banyak group sehari. Biarkan tab PostPintar & popup terbuka. Tutup popup untuk berhenti serta-merta.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="helper-auto-post-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={() => setAutoPost(true)} data-testid="helper-auto-post-accept">Saya faham, hidupkan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
